import * as React from 'react';

import { IconButton, Link, Popover, Text } from '@/components/ui';

import DataGridFilterPopover from './DataGridFilterPopover';

// DataGridHeaderCell — header content for a single column. Layout is a
// single-row flex with three slots:
//
//   [label + sort indicator]      [spacer]      [filter trigger]
//
// Click on the label area toggles sort (when sortable). Shift+click
// adds the column as a secondary sort key — TanStack's built-in
// `getToggleSortingHandler` honours the shift modifier so we don't have
// to plumb that ourselves. The sort indicator shows the direction (↑/↓)
// plus the priority number when multi-column sort is active (sort
// index > 0 means "this column is not the primary sort key"). With a
// single sort key the priority badge is hidden — it would just clutter
// the header.
//
// The filter trigger is a Popover anchored to a small icon button. The
// button switches `appearance` based on whether the column currently
// carries a filter:
//   - inactive: ghost / neutral (no paint, foreground icon)
//   - active:   outline / primary (brand-tinted pill — border + bg +
//                                   primary icon)
// The shift in `appearance` (not just tone) is what makes the state
// pop — `ghost → outline` adds a clearly visible chip around the icon.

const SORT_ICON_BY_DIRECTION = {
    asc: 'chevronUp',
    desc: 'chevronDown'
};

const DataGridHeaderCell = ({ header }) => {
    const column = header.column;
    const [filterOpen, setFilterOpen] = React.useState(false);
    const closeFilter = React.useCallback(() => setFilterOpen(false), []);
    // The built-in selection column has no label, no sort, no filter — render
    // an empty cell. Without this guard the fallback below would print the
    // column id ("selection") as the header label.
    if (column.columnDef.meta?.isSelection) {
        return null;
    }
    const sortDirection = column.getIsSorted(); // 'asc' | 'desc' | false
    const sortIndex = column.getSortIndex();    // -1 when not sorting on this column
    const sortPriorityCount = column.getCanMultiSort() ? header.getContext().table.getState().sorting.length : 0;
    const canSort = column.getCanSort();
    const canFilter = column.getCanFilter();
    const filterValue = column.getFilterValue();
    const hasFilter = filterValue !== undefined && filterValue !== '' &&
            !(Array.isArray(filterValue) && filterValue.length === 0) &&
            !(typeof filterValue === 'object' && !Array.isArray(filterValue) && filterValue !== null &&
                    (filterValue.from ?? '') === '' && (filterValue.to ?? '') === '');

    const label = typeof column.columnDef.header === 'string'
        ? column.columnDef.header
        : (column.columnDef.meta?.label ?? column.id);

    // Layout: [label-or-link] [sort indicator] [spacer] [filter trigger].
    // The label-or-link and sort indicator are two independent
    // components: the click target lives on the Link (bare appearance,
    // no chrome, hover bumps colour); the sort direction icon is a
    // disabled IconButton purely for visual orientation. Keeps each
    // concern in its own primitive.
    return (
        <div className="tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-inline-flex tw-items-center tw-gap-1">
                {canSort ? (
                    <Link
                        appearance="bare"
                        onClick={column.getToggleSortingHandler()}
                        text={label}
                        variant="overline"
                    />
                ) : (
                    <Text tone="inherit" variant="overline">{label}</Text>
                )}

                {sortDirection ? (
                    <>
                        <IconButton
                            appearance="ghost"
                            aria-label={i18next.t(
                                sortDirection === 'asc' ? 'datagrid.sort.asc' : 'datagrid.sort.desc',
                                { defaultValue: sortDirection === 'asc' ? 'Sorted ascending' : 'Sorted descending' }
                            )}
                            disabled
                            icon={SORT_ICON_BY_DIRECTION[sortDirection]}
                            size="tiny"
                            tone="neutral"
                        />
                        {sortPriorityCount > 1 ? (
                            <Text tone="inherit" variant="caption">{sortIndex + 1}</Text>
                        ) : null}
                    </>
                ) : null}
            </span>

            {canFilter ? (
                <Popover
                    align="end"
                    onOpenChange={setFilterOpen}
                    open={filterOpen}
                    trigger={
                        <IconButton
                            appearance={hasFilter ? 'outline' : 'ghost'}
                            aria-label={i18next.t('datagrid.filter.open', 'Filter column')}
                            icon="filter"
                            size="small"
                            tone={hasFilter ? 'primary' : 'neutral'}
                        />
                    }
                    content={
                        <div className="tw-p-3">
                            <DataGridFilterPopover column={column} onClose={closeFilter}/>
                        </div>
                    }
                />
            ) : null}
        </div>
    );
};

export default DataGridHeaderCell;
